import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as ImagePicker from 'expo-image-picker';

import { processSelectedMedia, SelectedMedia, FileNotSupported } from '@pletly/common';
import { showErrorToUser } from '@pletly/ui';
import { useLoadingOverlay } from '%context';
import { alerto as alert } from '%helpers/alert';
import { launchImageLibraryAsync } from './pickMedia/launchImageLibrary';

export const usePickedImages = (
	initialState: SelectedMedia[] = [],
	options?: ImagePicker.ImagePickerOptions,
) => {
	const { t } = useTranslation();
	const { showLoadingOverlay, closeLoadingOverlay, loading } = useLoadingOverlay();
	const [selectedImages, setSelectedImages] = useState<SelectedMedia[]>(initialState);

	const openImagePickerAsync = async (source: ImagePickerSource = 'gallery') => {
		showLoadingOverlay(t('post.adding-media', { count: 0 }));
		const permissionResult = CAMERA_SOURCES.includes(source)
			? await ImagePicker.requestCameraPermissionsAsync()
			: null;
		if (permissionResult?.granted === false) {
			alert(t('add-image.permission-to-access-camera-roll-is-required'));

			return closeLoadingOverlay();
		}

		const pickerOptions: ImagePicker.ImagePickerOptions = {
			base64: true,
			exif: true,
			...options,
			selectionLimit: options?.selectionLimit
				? options.selectionLimit - selectedImages.length
				: undefined,
		};
		try {
			const pickerResult =
				source === 'photo'
					? await ImagePicker.launchCameraAsync({
							...pickerOptions,
							mediaTypes: ImagePicker.MediaTypeOptions.Images,
						})
					: source === 'video'
						? await ImagePicker.launchCameraAsync({
								...pickerOptions,
								mediaTypes: ImagePicker.MediaTypeOptions.Videos,
							})
						: await launchImageLibraryAsync(pickerOptions);

			if (pickerResult.canceled === true) {
				return closeLoadingOverlay();
			}

			// selectionLimit is not implemented for web
			// https://docs.expo.dev/versions/latest/sdk/imagepicker/#imagepickeroptions
			const maxPossibleSelectionLimit = options?.selectionLimit
				? options.selectionLimit - selectedImages.length
				: Infinity;
			if (pickerResult.assets.length > maxPossibleSelectionLimit && options?.selectionLimit) {
				alert(`${options.selectionLimit} items max`);
			}

			const pickedMedia = pickerResult.assets.slice(0, maxPossibleSelectionLimit);
			showLoadingOverlay(t('post.adding-media', { count: pickedMedia.length }));
			const pickedAndProcessedMedia = await Promise.all(pickedMedia.map(processSelectedMedia));

			setSelectedImages([...selectedImages, ...pickedAndProcessedMedia]);
		} catch (error) {
			if (error instanceof FileNotSupported) {
				showErrorToUser(error);
			} else {
				console.error(error);
			}
		} finally {
			closeLoadingOverlay();
		}
	};

	return { selectedImages, openImagePickerAsync, setSelectedImages, loading };
};

export type ImagePickerSource = 'photo' | 'video' | 'gallery';

const CAMERA_SOURCES = ['photo', 'video'];
